/**
 * Valores monetários da plataforma.
 *
 * Todo valor financeiro trafega e é persistido como CENTAVOS inteiros (BRL).
 * A conversão reais <-> centavos é feita por string para evitar erros de
 * ponto flutuante (ex.: 0.1 + 0.2, 1.005 * 100).
 */

/** Maior valor aceito em qualquer campo monetário: R$ 100.000.000,00 */
export const MAX_CENTS = 10_000_000_000;

/** true se `value` é um inteiro de centavos válido (0 <= value <= MAX_CENTS). */
export function isValidCents(value: unknown): value is number {
  return typeof value === 'number' && Number.isSafeInteger(value) && value >= 0 && value <= MAX_CENTS;
}

/**
 * Converte uma parte inteira + parte decimal (strings só de dígitos) em centavos,
 * arredondando meio-para-cima na terceira casa sem aritmética de float.
 */
function digitsToCents(integerPart: string, decimalPart: string): number {
  const decimals = (decimalPart + '000').slice(0, 3);
  let cents = Number(integerPart || '0') * 100 + Number(decimals.slice(0, 2));
  if (Number(decimals[2]) >= 5) cents += 1;
  return cents;
}

/**
 * Converte um valor em reais para centavos inteiros.
 *
 * Aceita:
 * - number (ex.: 1234.56)
 * - string pt-BR ("1.234,56", "R$ 1.234,56", "1234,5")
 * - string com ponto decimal ("1234.56") quando não há vírgula e o ponto
 *   não está no formato de milhar ("1.234" é tratado como mil duzentos e trinta e quatro)
 *
 * Retorna NaN para entradas inválidas ou negativas — quem chama decide a mensagem de erro.
 */
export function toCents(reais: number | string): number {
  if (typeof reais === 'number') {
    if (!Number.isFinite(reais) || reais < 0) return NaN;
    // toFixed(3) evita notação exponencial; a terceira casa decide o arredondamento
    const [integerPart = '0', decimalPart = ''] = reais.toFixed(3).split('.');
    return digitsToCents(integerPart, decimalPart);
  }

  if (typeof reais !== 'string') return NaN;
  const cleaned = reais.replace(/R\$/gi, '').replace(/\s/g, '');
  if (cleaned === '' || cleaned.startsWith('-')) return NaN;

  let integerPart: string;
  let decimalPart: string;

  if (cleaned.includes(',')) {
    // Formato pt-BR: vírgula é decimal, pontos são separadores de milhar
    const parts = cleaned.split(',');
    if (parts.length !== 2) return NaN;
    integerPart = (parts[0] ?? '').replace(/\./g, '');
    decimalPart = parts[1] ?? '';
  } else {
    const dotParts = cleaned.split('.');
    const looksLikeThousands =
      dotParts.length > 2 || (dotParts.length === 2 && (dotParts[1] ?? '').length === 3);
    if (looksLikeThousands) {
      integerPart = dotParts.join('');
      decimalPart = '';
    } else {
      integerPart = dotParts[0] ?? '';
      decimalPart = dotParts[1] ?? '';
    }
  }

  if (!/^\d*$/.test(integerPart) || !/^\d*$/.test(decimalPart)) return NaN;
  if (integerPart === '' && decimalPart === '') return NaN;

  const cents = digitsToCents(integerPart, decimalPart);
  return Number.isSafeInteger(cents) ? cents : NaN;
}

const BRL_FORMATTER = new Intl.NumberFormat('pt-BR', {
  style: 'currency',
  currency: 'BRL',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

/** Formata centavos como moeda brasileira: 123456 -> "R$ 1.234,56". */
export function formatBRL(cents: number): string {
  const safeCents = Number.isFinite(cents) ? Math.trunc(cents) : 0;
  return BRL_FORMATTER.format(safeCents / 100);
}

const INTEGER_FORMATTER = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 });

/**
 * Converte centavos em texto editável pt-BR, sem símbolo: 123456 -> "1.234,56".
 * Útil para preencher inputs com máscara de moeda.
 */
export function centsToInputString(cents: number): string {
  if (!Number.isFinite(cents)) return '';
  const absolute = Math.abs(Math.trunc(cents));
  const reais = Math.floor(absolute / 100);
  const remainder = String(absolute % 100).padStart(2, '0');
  const sign = cents < 0 ? '-' : '';
  return `${sign}${INTEGER_FORMATTER.format(reais)},${remainder}`;
}

/**
 * Converte centavos inteiros em reais (number com até duas casas) para APIs
 * que trabalham com valor decimal, como o `transaction_amount` do Mercado Pago.
 * Monta o número a partir da string "reais.centavos" em vez de dividir por 100,
 * para não carregar erro de ponto flutuante. Retorna NaN se `cents` não for
 * um inteiro de centavos válido.
 */
export function centsToReais(cents: number): number {
  if (!isValidCents(cents)) return NaN;
  const reais = Math.floor(cents / 100);
  const remainder = String(cents % 100).padStart(2, '0');
  return Number(`${reais}.${remainder}`);
}
